import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import urls from '../util/urls';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';
import { Consent } from '../hooks/useConsents';

export default class ConsentService {
  ConsentAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.ConsentAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  fetchLatestConsentForm = (auth: ServiceAuth) => async (projectId: string) => {
    return this.ConsentAPI(auth).get(
      urls.api.consent.projectConsentForms(projectId),
    );
  };

  fetchConsentById =
    (auth: ServiceAuth) => async (projectId: string, consentId: string) => {
      return this.ConsentAPI(auth).get(
        urls.api.consent.projectConsentFormById(projectId, consentId),
      );
    };

  createConsentForm =
    (auth: ServiceAuth) => async (form: any, projectId: string) => {
      return this.ConsentAPI(auth).post(
        urls.api.consent.projectConsentForms(projectId),
        {
          ...form,
        },
      );
    };

  updateLatestConsentForm =
    (auth: ServiceAuth) =>
    async (form: Partial<Consent>, projectId: string) => {
      return this.ConsentAPI(auth).patch(
        urls.api.consent.projectConsentForms(projectId),
        form,
      );
    };

  publishConsentForm =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      major: boolean,
      formId: string,
      identifier: Consent['identifier'],
    ) => {
      return this.ConsentAPI(auth).post(
        urls.api.consent.projectConsentFormVersion(projectId),
        {
          major,
          id: formId,
          identifier,
        },
      );
    };

  fetchProjectConsents = (auth: ServiceAuth) => async (projectId: string) => {
    const result = await this.ConsentAPI(auth).get(
      urls.api.consent.projectConsentForms(projectId),
    );
    return result;
  };

  fetchPatientConsentDirectives =
    (auth: ServiceAuth) => async (projectId: string, patientId: string) => {
      return this.ConsentAPI(auth).get(
        urls.api.consent.projectConsents({
          patientId,
          projectId,
          status: 'active,proposed,rejected',
        }),
      );
    };

  createPatientConsentDirective =
    (auth: ServiceAuth) => async (body: Record<string, any>) => {
      return this.ConsentAPI(auth).post(urls.api.consent.projectConsents({}), {
        ...body,
      });
    };

  deleteProjectPatient =
    (auth: ServiceAuth) => async (projectId: string, patientId: string) => {
      return this.ConsentAPI(auth).delete(
        urls.api.consent.patient(projectId, patientId),
      );
    };
}
